import React from "react";

import ActionsDropdown from "../../../../../components/Dropdown/ActionsDropdown";
import { activityColumnAlign } from "./constants";
import {
  buildTradeCommandDisplay,
  partialLockNoteOf,
} from "./tradeCommandDisplay";
import { TradeCommandItemProps } from "./types";
import {
  AmountText,
  Cell,
  CorrectionNote,
  DetailsStack,
  InstrumentExchange,
  InstrumentSymbol,
  KebabAlign,
  LockedChip,
  MutedText,
  PositionStack,
  Row,
  TypeBadge,
  UserNote,
} from "./styles";

export const TradeCommandRow: React.FC<TradeCommandItemProps> = ({
  command,
  actions,
  investmentName,
  settlementAccountName,
}) => {
  const {
    dateText,
    typeLabel,
    typeTone,
    holdingText,
    detailsText,
    amountText,
    amountVariant,
    quantityText,
    correctionNote,
    lockReason,
    userNote,
  } = buildTradeCommandDisplay(command, investmentName, settlementAccountName);

  // `ActionsDropdown` filters on `show` itself, so gate on the same criterion —
  // otherwise a command with neither action would leave an empty menu.
  const hasActions = actions.some((action) => action.show);
  const partialLockNote = partialLockNoteOf(actions, correctionNote);

  // Read from the column list rather than repeated here: hardcoding it would
  // let a figure and the heading above it end up aligned differently.
  const amountAlign = activityColumnAlign("amount");
  const quantityAlign = activityColumnAlign("quantity");

  const quantityCell =
    command.quantity === null ? (
      <MutedText>{quantityText}</MutedText>
    ) : (
      quantityText
    );

  return (
    <Row>
      <Cell>{dateText}</Cell>
      <Cell>
        <TypeBadge $tone={typeTone}>{typeLabel}</TypeBadge>
      </Cell>
      <Cell>
        <PositionStack>
          <InstrumentSymbol>{holdingText}</InstrumentSymbol>
          {/* Guarded: the catalogue can carry an empty exchange, and an account-level command has no instrument. */}
          {command.instrument?.exchange && (
            <InstrumentExchange>
              {command.instrument.exchange}
            </InstrumentExchange>
          )}
        </PositionStack>
      </Cell>
      <Cell>
        <DetailsStack>
          <span>{detailsText}</span>
          {userNote && <UserNote>{userNote}</UserNote>}
          {partialLockNote && (
            <CorrectionNote>{partialLockNote}</CorrectionNote>
          )}
        </DetailsStack>
      </Cell>
      <Cell $align={quantityAlign}>{quantityCell}</Cell>
      <Cell $align={amountAlign}>
        <AmountText $variant={amountVariant}>{amountText}</AmountText>
      </Cell>
      <Cell>
        <KebabAlign>
          {hasActions ? (
            <ActionsDropdown
              compact
              dropdownActions={actions}
              testId={`trade-command-actions-${command.commandId}`}
            />
          ) : (
            <LockedChip role="note" title={lockReason} aria-label={lockReason}>
              Locked
            </LockedChip>
          )}
        </KebabAlign>
      </Cell>
    </Row>
  );
};
